import { useEffect, useRef, useState } from 'react'
import { Popconfirm, Tag, Tooltip } from 'antd'
import { DeleteOutlined, DownloadOutlined, EditOutlined, LoadingOutlined, ReloadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import StageAssetViewer, { type StageViewerKind } from './StageAssetViewer'
import './stage-asset-viewer.css'

/**
 * Shared "原图 → 透明底图 → 色底图 → 阴影图" stage-flow visuals used by both
 * the formal asset list (`Home.tsx` → `ChainRow`) and the "自动处理模板"
 * debug drawer (`AutoProcessTemplateBar.tsx`). Kept in one place so a
 * template's preview and a real asset's row always look and behave the
 * same way, per spec: "推荐优先复用正式图片列表的四阶段卡片组件".
 */

/** Grid-cell thumbnail with built-in retry-on-error (Transparent Thumbnail
 * Cold-Start P0, 2026-08-25): a `{stage}_thumb` derivative can still be
 * cold-generating server-side the very first time a row is ever viewed
 * (legacy chains saved before this same P0's backend warm-on-save fix,
 * or a brand-new upload's first paint) — under concurrent load that
 * sometimes surfaces as an outright `<img>` load error rather than just
 * being slow (confirmed on Preview: ~9-10s network failures under a
 * bursty cold list load), and a plain `<img>`/antd `Image` never retries
 * on its own, permanently showing a broken cell for the rest of the
 * session ("透明底图加载失败"). A cheap regeneration is idempotent and
 * fast on the very next attempt (confirmed: an immediate manual reload
 * after a first failure succeeds), so a couple of short, backed-off
 * retries here turn that permanent break into a brief, self-healing
 * delay for the user instead. Resets its own retry state whenever `src`
 * itself changes (a different chain/generation/cache-bust token), so a
 * genuinely new URL always gets its own fresh attempts. */
function SelfHealingThumb({
  src,
  alt,
  onOpen,
}: {
  src: string
  alt: string
  onOpen: () => void
}) {
  const { t } = useTranslation('autoProcess')
  const [attempt, setAttempt] = useState(0)
  const retriesRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    retriesRef.current = 0
    setAttempt(0)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [src])

  const effectiveSrc = attempt === 0 ? src : `${src}${src.includes('?') ? '&' : '?'}retry=${attempt}`

  return (
    <button type="button" className="chain-thumb-open" onClick={onOpen} aria-label={t('stages.viewAlt', { label: alt })}>
      <img
        src={effectiveSrc}
        alt={alt}
        onError={() => {
          if (retriesRef.current < 2) {
            retriesRef.current += 1
            const delayMs = retriesRef.current === 1 ? 1200 : 3500
            timerRef.current = setTimeout(() => setAttempt((a) => a + 1), delayMs)
          }
        }}
      />
    </button>
  )
}

export function EditArrow({
  onClick,
  disabled,
  label,
}: {
  onClick?: () => void
  disabled?: boolean
  label: string
}) {
  const { t } = useTranslation('autoProcess')

  return (
    <div className="chain-arrow">
      <div className="chain-arrow-line" />
      <Tooltip title={disabled ? t('stages.nextStageLocked') : label}>
        <button type="button" className="edit-node" disabled={disabled} onClick={onClick} aria-label={label}>
          <EditOutlined />
        </button>
      </Tooltip>
    </div>
  )
}

export function AssetCell({
  label,
  url,
  previewUrl,
  fullUrl,
  downloadUrl,
  viewerKind = 'original',
  checker,
  statusLabel,
  statusColor,
  subtitle,
  meta,
  error,
  busy,
  onDelete,
  onRetry,
  onDownload,
  showRetry,
  retryLabel,
  emptySlot,
}: {
  label: string
  url?: string | null
  /** Display-tier URL for the contained StageAssetViewer (proxy /
   * transparent_proxy). Must NOT be the multi-MB transparent full PNG when
   * a proxy exists. */
  previewUrl?: string | null
  /** Optional full-resolution URL — only loaded if the user clicks
   * 「查看原始尺寸」 inside the viewer. */
  fullUrl?: string | null
  downloadUrl?: string | null
  viewerKind?: StageViewerKind
  checker?: boolean
  statusLabel?: string
  statusColor?: string
  subtitle?: string
  meta?: string
  error?: string | null
  busy?: boolean
  onDelete?: () => void
  onRetry?: () => void
  onDownload?: () => void
  showRetry?: boolean
  retryLabel?: string
  emptySlot?: React.ReactNode
}) {
  const { t } = useTranslation('autoProcess')
  const [viewerOpen, setViewerOpen] = useState(false)
  const displaySrc = previewUrl || url || null
  const openViewer = () => {
    if (displaySrc) setViewerOpen(true)
  }

  return (
    <div className="chain-cell">
      <div className={`chain-thumb ${checker ? 'checkerboard' : ''}`}>
        {url ? (
          <SelfHealingThumb src={url} alt={label} onOpen={openViewer} />
        ) : busy ? (
          <div className="chain-thumb-skeleton">
            <LoadingOutlined spin style={{ fontSize: 18 }} />
          </div>
        ) : emptySlot ? (
          emptySlot
        ) : statusLabel && (statusLabel.includes('生成') || statusLabel.includes('排队')) ? (
          <div className="chain-thumb-skeleton">
            <LoadingOutlined spin style={{ fontSize: 18 }} />
          </div>
        ) : (
          <span className="chain-thumb-placeholder">{label}</span>
        )}
        {busy && url && (
          <div className="chain-thumb-busy-overlay">
            <LoadingOutlined spin style={{ fontSize: 18 }} />
          </div>
        )}
        <div className="chain-thumb-actions">
          {showRetry && (
            <Tooltip title={retryLabel || t('stages.regenerate')}>
              <button type="button" className="thumb-icon-btn" onClick={onRetry}>
                <ReloadOutlined />
              </button>
            </Tooltip>
          )}
          {onDownload && (
            <Tooltip title={t('stages.downloadLabel', { label })}>
              <button
                type="button"
                className="thumb-icon-btn"
                onClick={(e) => {
                  e.stopPropagation()
                  onDownload()
                }}
                data-testid="chain-stage-download-btn"
              >
                <DownloadOutlined />
              </button>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title={t('stages.deleteAsset')}>
              <Popconfirm title={t('stages.confirmDelete')} onConfirm={onDelete}>
                <button type="button" className="thumb-icon-btn thumb-icon-btn-danger" onClick={(e) => e.stopPropagation()}>
                  <DeleteOutlined />
                </button>
              </Popconfirm>
            </Tooltip>
          )}
        </div>
      </div>
      {subtitle && (
        <Tooltip title={subtitle}>
          <div className="chain-cell-subtitle">{subtitle}</div>
        </Tooltip>
      )}
      <div className="chain-cell-meta-row">
        {meta && <span className="chain-cell-meta">{meta}</span>}
        {statusLabel && (
          <Tag color={statusColor} className="chain-status-tag">
            {statusLabel}
          </Tag>
        )}
      </div>
      {error && (
        <Tooltip title={error}>
          <div className="chain-cell-error">{t('stages.viewErrorDetails')}</div>
        </Tooltip>
      )}
      {displaySrc && (
        <StageAssetViewer
          open={viewerOpen}
          title={subtitle ? `${label} · ${subtitle}` : label}
          kind={viewerKind}
          displayUrl={displaySrc}
          fullUrl={fullUrl && fullUrl !== displaySrc ? fullUrl : null}
          downloadUrl={downloadUrl}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </div>
  )
}

/** Cache-busting query token so the browser always fetches fresh bytes
 * after a stage is regenerated, even though the underlying file path never
 * changes (both real chains and templates rewrite the same filename). */
export function bustUrl(url: string | null | undefined, token: string | number | null | undefined): string | null {
  if (!url) return null
  const t = encodeURIComponent(String(token ?? ''))
  return `${url}${url.includes('?') ? '&' : '?'}t=${t}`
}

export type FourStageCellProps = Omit<Parameters<typeof AssetCell>[0], 'label'>
export type FourStageEdgeProps = Parameters<typeof EditArrow>[0]

/**
 * The four static column titles ("原图/透明底图/色底图/阴影图") sized with the
 * exact same 4-asset + 3-arrow track widths as `<FourStageImageFlow>` below,
 * so a header rendered once above several rows (formal asset list) and a
 * header rendered once above a single row (template test-image drawer)
 * always line up pixel-for-pixel with the cells beneath them.
 */
export function FourStageFlowHeader() {
  const { t } = useTranslation('autoProcess')

  return (
    <div className="four-stage-flow-header">
      <div className="four-stage-flow-header-cell">{t('stages.original')}</div>
      <div className="four-stage-flow-header-arrow" />
      <div className="four-stage-flow-header-cell">{t('stages.transparent')}</div>
      <div className="four-stage-flow-header-arrow" />
      <div className="four-stage-flow-header-cell">{t('stages.background')}</div>
      <div className="four-stage-flow-header-arrow" />
      <div className="four-stage-flow-header-cell">{t('stages.shadow')}</div>
    </div>
  )
}

/**
 * The single "原图 → 透明底图 → 色底图 → 阴影图" row shared by the formal
 * asset list (`Home.tsx` → `ChainRow`) and the "自动处理模板" test-image
 * drawer (`AutoProcessTemplateBar.tsx`) — same cell component, same edit
 * connectors, same evenly-distributed column widths, so the two contexts
 * can never visually drift apart (spec 四: "完全复用下方四阶段图片展示 UI").
 * Callers own everything scene-specific (checkbox column, per-row actions,
 * data source) and only pass in per-cell/per-edge state here.
 */
export function FourStageImageFlow({
  original,
  transparent,
  background,
  shadow,
  toTransparent,
  toBackground,
  toShadow,
}: {
  original: FourStageCellProps
  transparent: FourStageCellProps
  background: FourStageCellProps
  shadow: FourStageCellProps
  toTransparent: FourStageEdgeProps
  toBackground: FourStageEdgeProps
  toShadow: FourStageEdgeProps
}) {
  const { t } = useTranslation('autoProcess')

  return (
    <div className="four-stage-flow-row">
      <AssetCell label={t('stages.original')} {...original} />
      <EditArrow {...toTransparent} />
      <AssetCell label={t('stages.transparent')} {...transparent} />
      <EditArrow {...toBackground} />
      <AssetCell label={t('stages.background')} {...background} />
      <EditArrow {...toShadow} />
      <AssetCell label={t('stages.shadow')} {...shadow} />
    </div>
  )
}
